/**
 * @file navigation.ts
 * @description Настройки навигации сайта — документ части MENUS (phase19.16,
 * партия «SEO-техника», v0.19.65; site-builder.md SB146; проект данных
 * docs/phases/phase19.16-materials/db-design-seo-tech.md §1.4; решение
 * оркестратора §3а п. 3 — отдельный документ, а не ключ внутри `menus`).
 *
 * Сейчас здесь один переключатель «Показывать хлебные крошки» (панель
 * конструктора «Весь сайт → Навигация», вкладка «Крошки»). Хранение:
 * `SiteDraft.navigationOptions` (черновик, `{}` — умолчания платформы) и
 * `SiteRevision.navigation` (снимок; NULL — версии до v0.19.65).
 *
 * Почему не ключ внутри `SiteRevision.menus`: снимок меню читается строгой
 * схемой (`siteMenusSnapshotSchema`), и лишний ключ при откате кода
 * превратил бы живое меню сайта в умолчание (db-design §1.4, вариант Б).
 *
 * Зачем в shared: одно умолчание и одно чтение у API (снимок, site-config),
 * кабинета (панель «Навигация», «Что изменено») и витрины (решение
 * `showBreadcrumbs`, site-seo/breadcrumbs.ts).
 */

import { z } from "zod";

import { deepFreeze } from "./deep-freeze.js";

/** Строгая схема записи (PATCH части): лишний ключ — ошибка. */
export const siteNavigationOptionsSchema = z.strictObject({
  /** «Показывать хлебные крошки» на сайте (SB146); страница может перекрыть. */
  breadcrumbs: z.boolean(),
});

export type SiteNavigationOptions = z.infer<typeof siteNavigationOptionsSchema>;

/** Умолчание платформы: крошки включены (SB146, как витрина рисовала до v0.19.65). */
export const DEFAULT_SITE_NAVIGATION: Readonly<SiteNavigationOptions> = deepFreeze<SiteNavigationOptions>({
  breadcrumbs: true,
});

/** Замечание чтения: путь в документе и причина (для журнала сервера). */
export interface SiteNavigationReadIssue {
  path: string;
  reason: string;
}

export interface SiteNavigationReadResult {
  options: SiteNavigationOptions;
  replaced: SiteNavigationReadIssue[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Чтение `SiteDraft.navigationOptions` / `SiteRevision.navigation` по полям
 * (как `readCatalogOptions`): никогда не бросает. NULL, отсутствие и `{}` —
 * умолчания без замечаний (версии до v0.19.65, магазин ничего не трогал);
 * не объект или поле не того типа — умолчание с замечанием. Лишние ключи
 * (документ от будущей версии кода) молча игнорируются — откат безопасен.
 */
export function readNavigationOptionsWithIssues(raw: unknown): SiteNavigationReadResult {
  const replaced: SiteNavigationReadIssue[] = [];
  const options: SiteNavigationOptions = { ...DEFAULT_SITE_NAVIGATION };
  if (raw === null || raw === undefined) return { options, replaced };
  if (!isPlainObject(raw)) {
    replaced.push({ path: "", reason: "Настройки навигации не прочитаны — взяты умолчания" });
    return { options, replaced };
  }
  const breadcrumbs = raw["breadcrumbs"];
  if (typeof breadcrumbs === "boolean") options.breadcrumbs = breadcrumbs;
  else if (breadcrumbs !== undefined) {
    replaced.push({ path: "breadcrumbs", reason: "Хлебные крошки: значение не прочитано — показывать" });
  }
  return { options, replaced };
}

/** То же без замечаний — для витрины, кабинета и мест, где журнал не нужен. */
export function readNavigationOptions(raw: unknown): SiteNavigationOptions {
  return readNavigationOptionsWithIssues(raw).options;
}

// ── «Что изменено» ──

/** Строка «Что изменено» (та же форма, что `SiteViewDiffItem` в diff.ts). */
export interface SiteNavigationDiffItem {
  path: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** Подписи полей документа навигации. */
export const SITE_NAVIGATION_DIFF_LABELS = {
  breadcrumbs: "Хлебные крошки",
} as const satisfies Record<keyof SiteNavigationOptions, string>;

/**
 * Различия документа навигации словами — строки части MENUS рядом с
 * пунктами меню: «Хлебные крошки: показывать → не показывать».
 */
export function diffSiteNavigation(before: SiteNavigationOptions, after: SiteNavigationOptions): SiteNavigationDiffItem[] {
  const word = (on: boolean): string => (on ? "показывать" : "не показывать");
  if (before.breadcrumbs === after.breadcrumbs) return [];
  return [
    {
      path: "navigation.breadcrumbs",
      label: SITE_NAVIGATION_DIFF_LABELS.breadcrumbs,
      before: word(before.breadcrumbs),
      after: word(after.breadcrumbs),
    },
  ];
}
